// ---------------------------------------------------------------------------
// AI Feedback Signals — captures human corrections to AI outputs
// ---------------------------------------------------------------------------
// Sprint 1 Stream C (C2): Foundation for AI continuous learning.
// When a human edits, accepts, rejects, or ignores an AI suggestion,
// the before/after + context is recorded here for future model fine-tuning.
//
// Signal types:
// - email_subject_edit: AI suggested subject, human modified it
// - email_body_edit: AI generated body, human edited
// - ai_suggestion_accepted: AI suggestion used as-is
// - ai_suggestion_rejected: AI suggestion dismissed
// - workflow_ai_override: AI recommended action, human chose differently
// - chat_response_edit: AI drafted response, human modified
//
// Privacy: ai_output and human_action contain CONTENT, not PII identifiers.
// Context may contain entity references but not raw PII.
// GDPR erasure: delete by tenant_id (standard cascade).
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, jsonb, timestamp, index } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const aiFeedbackSignals = pgTable(
  "ai_feedback_signals",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    signalType: text("signal_type").notNull(),
    /** The AI output that was presented to the human */
    aiOutputId: text("ai_output_id"), // Optional reference to the AI generation
    aiOutput: jsonb("ai_output").$type<Record<string, unknown>>().notNull(),
    /** What the human did (edit, accept, reject, ignore) + the result */
    humanAction: jsonb("human_action").$type<Record<string, unknown>>().notNull(),
    /** Contextual metadata: entity type/id, workflow, campaign, etc. */
    context: jsonb("context").$type<Record<string, unknown>>().default({}),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_ai_feedback_signals_tenant").on(table.tenantId),
    index("idx_ai_feedback_signals_type").on(table.tenantId, table.signalType),
    index("idx_ai_feedback_signals_created").on(table.tenantId, table.createdAt),
  ],
);

// ---------------------------------------------------------------------------
// Insert helper — used by API routes when capturing feedback
// ---------------------------------------------------------------------------

export interface InsertAiFeedbackSignal {
  tenantId: string;
  signalType: string;
  aiOutputId?: string;
  aiOutput: Record<string, unknown>;
  humanAction: Record<string, unknown>;
  context?: Record<string, unknown>;
}

/**
 * Insert an AI feedback signal into the tenant database.
 * Returns the created signal ID.
 *
 * Usage:
 * ```ts
 * const id = await insertAiFeedbackSignal(db, {
 *   tenantId: "...",
 *   signalType: "email_subject_edit",
 *   aiOutput: { subject: "AI suggested subject" },
 *   humanAction: { action: "edit", result: "Human edited subject" },
 * });
 * ```
 */
export async function insertAiFeedbackSignal(
  // Accept any Drizzle database instance (Neon HTTP or postgres.js — both implement insert().values().returning())
  db: { insert: (table: typeof aiFeedbackSignals) => any },
  signal: InsertAiFeedbackSignal,
): Promise<string> {
  const [result] = await db
    .insert(aiFeedbackSignals)
    .values({
      tenantId: signal.tenantId,
      signalType: signal.signalType,
      aiOutputId: signal.aiOutputId ?? null,
      aiOutput: signal.aiOutput,
      humanAction: signal.humanAction,
      context: signal.context ?? {},
    })
    .returning({ id: aiFeedbackSignals.id });
  return result.id;
}
